class ProductMedia extends HTMLElement {
  constructor() {
    super();
    this.swiperLoaded = false;
    this.initialized = false;
  }

  // Function to dynamically load Swiper
  async loadSwiper() {
    if (typeof window.Swiper !== 'undefined') {
      this.swiperLoaded = true;
      return Promise.resolve();
    }
    
    if (this.swiperLoading) {
      return this.swiperLoading;
    }
    
    try {
      this.swiperLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = window.swiperUrl;
        script.async = true;
        
        script.onload = () => {
          this.swiperLoaded = true;
          this.swiperLoading = null;
          resolve();
        };
        script.onerror = () => {
          console.error('Failed to load Swiper.js');
          this.swiperLoading = null;
          reject(new Error('Swiper loading failed'));
        };
        document.head.appendChild(script);
      });
      
      return this.swiperLoading;
    } catch (error) {
      console.error('Error loading Swiper:', error);
      this.swiperLoading = null;
      throw error;
    }
  }

  // Idle loading - load during browser idle time
  static idleLoadSwiper() {
    const hasProductMedia = document.querySelector('product-media');
    if (!hasProductMedia) return;
    
    const loadSwiperIdle = () => {
      if (typeof window.Swiper !== 'undefined') return;
      
      const script = document.createElement('script');
      script.src = window.swiperUrl;
      script.async = true;
      document.head.appendChild(script);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadSwiperIdle, { timeout: 2000 });
    } else {
      setTimeout(loadSwiperIdle, 1000);
    }
  }

  // connect component
  connectedCallback() {
    // Try immediate initialization first for critical above-the-fold content
    const rect = this.getBoundingClientRect();
    const isAboveFold = rect.top < window.innerHeight;
    
    if (isAboveFold) {
      this.init();
    } else {
      // Use Intersection Observer for below-the-fold content
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.initialized) {
            this.init();
            observer.unobserve(this);
          }
        });
      }, {
        rootMargin: '100px' // Start loading 100px before element comes into view
      });

      observer.observe(this);
    }
  }

  async init() {
    if (this.initialized) return;
    this.initialized = true;

    const breakpoint = window.matchMedia("(max-width: 990px)");
    let productMediaSlider;
    let onClick;
    
    const checkBreakpoint = () => {
      const allSliders = document.querySelectorAll(".swiper-product-media");
      
      allSliders.forEach((slider) => {
        if (slider.dataset.desktopLayout === "slider") {
          this.enableSlider(slider);
        } else {
          // display in stacked format on desktop
          if (breakpoint.matches === false) {
            this.disableSlider(slider);
            // display in slider format on mobile
          } else if (breakpoint.matches === true) {
            if (!slider.classList.contains("swiper-initialized")) {
              this.enableSlider(slider);
            }
          }
        }
        
        if (breakpoint.matches === false) {
          this.enableZoom();
          // don't enable zoom on mobile.
        } else if (breakpoint.matches === true) {
          this.disableZoom();
        }
      });
    };
    
    this.enableSlider = async (slider) => {
      try {
        // Load Swiper if not already loaded
        await this.loadSwiper();
        
        // Verify Swiper is available
        if (typeof window.Swiper === 'undefined') {
          throw new Error('Swiper failed to load');
        }
        
        const sliderId = slider.id;
        
        // Validate slider element
        if (!slider || !sliderId) {
          console.error('Invalid slider element or missing ID');
          return;
        }
        
        slider.classList.remove("product-grid");
        const slideElements = slider.querySelectorAll(".swiper-slide:not(.swiper-slide-duplicate)");
        const slideCount = slideElements.length;
        
        // Check wrapper element
        const wrapper = slider.querySelector('.swiper-wrapper');
        
        if (!wrapper) {
          console.error('No .swiper-wrapper found in slider');
          slider.classList.add("product-grid");
          return;
        }
        
        const thumbsId = `${sliderId}--thumbs`;
        const thumbsElement = document.getElementById(thumbsId);
        
        let thumbsSwiper;
        
        if (thumbsElement) {
          const thumbsSlides = thumbsElement.querySelectorAll('.swiper-slide');
          const thumbsWrapper = thumbsElement.querySelector('.swiper-wrapper');
          
          if (thumbsSlides.length > 0 && thumbsWrapper) {
            try {
              thumbsSwiper = new window.Swiper(`#${thumbsId}`, {
                loop: false,
                slidesPerView: 5,
                spaceBetween: 5,
                slideToClickedSlide: true,
                watchSlidesProgress: true,
              });
            } catch (thumbsError) {
              console.error('Failed to create thumbs swiper:', thumbsError);
              // Continue without thumbs swiper
            }
          }
        }
        
        if (slideCount > 1) {
          const config = {
            loop: true,
            zoom: {
              maxRatio: 5,
              containerClass: `swiper-zoom-container`,
            },
            pagination: {
              el: `#${sliderId} .swiper-pagination`,
              clickable: true,
            },
            navigation: {
              nextEl: `#${sliderId} .swiper-button-next`,
              prevEl: `#${sliderId} .swiper-button-prev`,
            },
          };
          
          // Only add thumbs if we have thumbsSwiper
          if (thumbsSwiper) {
            config.thumbs = {
              swiper: thumbsSwiper,
            };
          }
          
          productMediaSlider = new window.Swiper(`#${sliderId}`, config);
        }
      } catch (error) {
        console.error('Failed to initialize Swiper:', error);
        
        // Fallback to grid layout if Swiper fails to load
        slider.classList.add("product-grid");
      }
    };

    this.disableSlider = () => {
      if (productMediaSlider !== undefined) {
        productMediaSlider.destroy(true, true);
      }
    };

    this.enableZoom = () => {
      const mainProduct = document.getElementById("MainProduct");
      if (mainProduct) {
        mainProduct.classList.add("product-grid");
      }
      
      const slides = document.getElementsByClassName("swiper-zoom-container");
      Array.from(slides).forEach((slide) => {
        const onMouseMove = function (event) {
          const x = (event.offsetX / slide.offsetWidth) * 100;
          const y = (event.offsetY / slide.offsetHeight) * 100;
          slide.style.backgroundPosition = x + "% " + y + "%";
        };
        // remove zoom
        const onMouseOut = function () {
          slide.removeEventListener("mousemove", onMouseMove);
          slide.removeEventListener("mouseout", onMouseOut);
          slide.classList.remove("js-zoom-container");
          slide.style.backgroundImage = "";
        };
        const onClick = function () {
          const imageUrl = slide.children[0].src;
          slide.style.backgroundImage = `url(${imageUrl})`;
          slide.classList.add("js-zoom-container");
          slide.addEventListener("mousemove", onMouseMove);
          slide.addEventListener("mouseout", onMouseOut);
        };
        slide.addEventListener("click", onClick);
      });
    };

    this.disableZoom = () => {
      const slides = document.getElementsByClassName("swiper-zoom-container");
      Array.from(slides).forEach((slide) => {
        slide.removeEventListener("click", onClick);
      });
    };

    breakpoint.addListener(checkBreakpoint);
    checkBreakpoint(); // first check
  }
}

// Initialize idle loading when page loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ProductMedia.idleLoadSwiper();
  });
} else {
  ProductMedia.idleLoadSwiper();
}

customElements.define("product-media", ProductMedia);
